import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface Transaction {
  id: string;
  transactionId: string;
  type: string;
  status: string;
  amount: number;
  signature: string | null;
  errorMessage: string | null;
  fromWalletId: string;
  toWalletId: string;
  metadata: any;
  createdAt: string;
  updatedAt: string;
}

export const FailedTransactions: React.FC = () => {
  const [failedTransactions, setFailedTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryingTransaction, setRetryingTransaction] = useState<string | null>(null);

  useEffect(() => {
    loadFailedTransactions();
  }, []);

  const loadFailedTransactions = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const response = await axios.get(`${API_BASE_URL}/transaction/filter/search?status=failed`);
      setFailedTransactions(response.data);
    } catch (err) {
      setError('Failed to load failed transactions');
      console.error('Error loading failed transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetryTransaction = async (transactionId: string) => {
    try {
      setRetryingTransaction(transactionId);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      await axios.post(`${API_BASE_URL}/transaction/${transactionId}/retry`);
      await loadFailedTransactions(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to retry transaction');
    } finally {
      setRetryingTransaction(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 8,
      maximumFractionDigits: 8,
    });
  };

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 8)}...${address.slice(-8)}`;
  };

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading failed transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Failed Transactions</h2>
        <button
          onClick={loadFailedTransactions}
          className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-red-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  From
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Error Message
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {failedTransactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-red-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900">
                      {truncateAddress(transaction.transactionId)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      {transaction.type.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 font-mono">
                      {formatAmount(transaction.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900">
                      {transaction.fromWalletId ? truncateAddress(transaction.fromWalletId) : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900">
                      {transaction.toWalletId ? truncateAddress(transaction.toWalletId) : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-red-600 max-w-xs truncate" title={transaction.errorMessage || ''}>
                      {transaction.errorMessage || 'Unknown error'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(transaction.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleRetryTransaction(transaction.transactionId)}
                      disabled={retryingTransaction === transaction.transactionId}
                      className="text-red-600 hover:text-red-900 disabled:text-red-400 font-medium"
                    >
                      {retryingTransaction === transaction.transactionId ? 'Retrying...' : 'Retry'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {failedTransactions.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No failed transactions found. 🎉</p>
          </div>
        )}
      </div>

      {failedTransactions.length > 0 && (
        <div className="mt-6 text-sm text-gray-600">
          <p>Total Failed Transactions: {failedTransactions.length}</p>
        </div>
      )}
    </div>
  );
};
